import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, Plus, Send, ArrowDownLeft, ArrowUpRight,
  X, CreditCard, Smartphone, Wallet as WalletIcon, Loader2,
} from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ORANGE = "#E05D3A";
const ORANGE_DARK = "#C24B2C";
const ORANGE_LIGHT = "#FEF0EE";
const GREEN = "#1DB954";
const GREEN_LIGHT = "#E8F7EE";
const RED = "#E53935";
const RED_LIGHT = "#FDECEC";

const FILTERS_AR = [
  { key: "all",      label: "جميع العمليات" },
  { key: "topup",    label: "الشحن" },
  { key: "send",     label: "الحوالات" },
  { key: "receive",  label: "الواردة" },
  { key: "purchase", label: "المشتريات" },
];
const FILTERS_EN = [
  { key: "all",      label: "All" },
  { key: "topup",    label: "Top-ups" },
  { key: "send",     label: "Sent" },
  { key: "receive",  label: "Received" },
  { key: "purchase", label: "Purchases" },
];

function Riyal({ className = "w-4 h-4" }) {
  return <span className={`font-bold ${className}`}>﷼</span>;
}

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function TxRow({ tx, lang }) {
  const isIn = tx.amount > 0;
  return (
    <div className="flex items-center gap-4 py-4 border-b border-[#F2F2F2] last:border-b-0">
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: isIn ? GREEN_LIGHT : RED_LIGHT }}
      >
        {isIn
          ? <ArrowDownLeft className="w-5 h-5" style={{ color: GREEN }} strokeWidth={2.6} />
          : <ArrowUpRight   className="w-5 h-5" style={{ color: RED }} strokeWidth={2.6} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[15px] text-[#1A1A1A] truncate">
          {lang === "ar" ? tx.label_ar : tx.label_en}
        </p>
        <p className="text-xs text-[#8B8B8B] mt-0.5">
          {lang === "ar" ? tx.sublabel_ar : tx.sublabel_en}
        </p>
        {tx.note && <p className="text-[11px] text-[#B3B3B3] mt-0.5">{tx.note}</p>}
      </div>
      <div
        className="flex items-center gap-1 font-bold"
        style={{ color: isIn ? GREEN : RED }}
        dir="ltr"
      >
        <span>{Math.abs(tx.amount).toFixed(2)}</span>
        <Riyal />
      </div>
    </div>
  );
}

function TopupSheet({ open, onClose, onSuccess }) {
  const { lang } = useLang();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const quick = [50, 100, 200, 500];

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      toast.error(lang === "ar" ? "أدخل مبلغاً صحيحاً" : "Enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/topup", { amount: num, method });
      toast.success(lang === "ar" ? "تم شحن المحفظة بنجاح" : "Wallet topped up");
      onSuccess(data.balance);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "تعذّر الشحن" : "Top-up failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose} data-testid="topup-sheet">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 animate-in slide-in-from-bottom-4 duration-200 max-h-[92vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom,0px)+24px)]"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-black text-[#1A1A1A]">
            {lang === "ar" ? "إضافة رصيد" : "Add balance"}
          </h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center" data-testid="topup-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
          {lang === "ar" ? "المبلغ بالريال" : "Amount in SAR"}
        </label>
        <div className="relative mb-3">
          <input
            type="number"
            inputMode="decimal"
            min="1"
            max="5000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full text-2xl font-bold text-center py-4 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] outline-none transition-colors"
            data-testid="topup-amount-input"
            autoFocus
          />
        </div>

        <div className="grid grid-cols-4 gap-2 mb-5">
          {quick.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setAmount(String(q))}
              className={`py-2.5 rounded-xl font-bold text-sm transition-colors ${
                amount === String(q)
                  ? "bg-[#E05D3A] text-white"
                  : "bg-[#FEF0EE] text-[#E05D3A] hover:bg-[#FCE3DD]"
              }`}
              data-testid={`topup-quick-${q}`}
            >
              {q} ﷼
            </button>
          ))}
        </div>

        <p className="text-xs font-semibold text-[#1A1A1A] mb-2">
          {lang === "ar" ? "طريقة الدفع" : "Payment method"}
        </p>
        <div className="space-y-2 mb-6">
          {[
            { key: "card", icon: CreditCard, ar: "بطاقة (مدى، فيزا، ماستركارد)", en: "Card (Mada, Visa, MC)" },
            { key: "apple_pay", icon: Smartphone, ar: "Apple Pay", en: "Apple Pay" },
            { key: "stc_pay", icon: WalletIcon, ar: "STC Pay", en: "STC Pay" },
          ].map(({ key, icon: Icon, ar, en }) => (
            <button
              key={key}
              type="button"
              onClick={() => setMethod(key)}
              className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-start transition-colors ${
                method === key ? "border-[#E05D3A] bg-[#FEF0EE]" : "border-[#EAEAEA] bg-white"
              }`}
              data-testid={`topup-method-${key}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                method === key ? "bg-[#E05D3A] text-white" : "bg-[#F5F5F5] text-[#1A1A1A]"
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="flex-1 font-semibold text-[14px]">
                {lang === "ar" ? ar : en}
              </span>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                method === key ? "border-[#E05D3A]" : "border-[#CCCCCC]"
              }`}>
                {method === key && <div className="w-2.5 h-2.5 rounded-full bg-[#E05D3A]" />}
              </div>
            </button>
          ))}
        </div>

        <button
          type="submit"
          disabled={loading || !amount}
          className="w-full rounded-2xl bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-4 font-bold text-[15px] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="topup-submit"
        >
          {loading && <Loader2 className="w-5 h-5 animate-spin" />}
          {lang === "ar"
            ? (loading ? "جاري الشحن..." : "تأكيد الشحن")
            : (loading ? "Processing..." : "Confirm top-up")}
        </button>
      </form>
    </div>
  );
}

function SendSheet({ open, onClose, onSuccess, balance }) {
  const { lang } = useLang();
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      toast.error(lang === "ar" ? "أدخل مبلغاً صحيحاً" : "Enter a valid amount");
      return;
    }
    if (num > balance) {
      toast.error(lang === "ar" ? "رصيدك غير كافٍ" : "Insufficient balance");
      return;
    }
    if (!phone.trim()) {
      toast.error(lang === "ar" ? "أدخل رقم الجوال" : "Enter recipient phone");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/send", {
        recipient_phone: phone.trim(),
        amount: num,
        note: note.trim(),
      });
      toast.success(lang === "ar" ? "تم إرسال الرصيد بنجاح" : "Transfer completed");
      onSuccess(data.balance);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "تعذّر الإرسال" : "Transfer failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose} data-testid="send-sheet">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 animate-in slide-in-from-bottom-4 duration-200 max-h-[92vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom,0px)+24px)]"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-black text-[#1A1A1A]">
            {lang === "ar" ? "إرسال رصيد" : "Send balance"}
          </h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center" data-testid="send-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-[#6B6B6B] mb-4">
          {lang === "ar"
            ? `الرصيد المتاح: ${balance.toFixed(2)} ﷼`
            : `Available: ${balance.toFixed(2)} SAR`}
        </p>

        <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
          {lang === "ar" ? "رقم جوال المستلم" : "Recipient phone"}
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+966 5X XXX XXXX"
          dir="ltr"
          className="w-full py-3 px-4 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] outline-none transition-colors mb-4"
          data-testid="send-phone-input"
        />

        <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
          {lang === "ar" ? "المبلغ" : "Amount"}
        </label>
        <input
          type="number"
          inputMode="decimal"
          min="1"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          className="w-full text-xl font-bold text-center py-3 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] outline-none transition-colors mb-4"
          data-testid="send-amount-input"
        />

        <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
          {lang === "ar" ? "ملاحظة (اختياري)" : "Note (optional)"}
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={80}
          placeholder={lang === "ar" ? "هدية، شكراً، إلخ" : "Gift, thanks, etc."}
          className="w-full py-3 px-4 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] outline-none transition-colors mb-6"
          data-testid="send-note-input"
        />

        <button
          type="submit"
          disabled={loading || !amount || !phone}
          className="w-full rounded-2xl bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-4 font-bold text-[15px] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="send-submit"
        >
          {loading && <Loader2 className="w-5 h-5 animate-spin" />}
          {lang === "ar"
            ? (loading ? "جاري الإرسال..." : "تأكيد الإرسال")
            : (loading ? "Sending..." : "Confirm transfer")}
        </button>
      </form>
    </div>
  );
}

export default function Wallet() {
  const navigate = useNavigate();
  const { lang, isRTL } = useLang();
  const { user } = useAuth();
  const Chevron = isRTL ? ChevronRight : ChevronLeft;
  const [filter, setFilter] = useState("all");
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupOpen, setTopupOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const filters = lang === "ar" ? FILTERS_AR : FILTERS_EN;

  const loadWallet = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet");
      setBalance(data.balance || 0);
      setTransactions(data.transactions || []);
    } catch {
      setBalance(0);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadWallet();
    else setLoading(false);
  }, [user, loadWallet]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center min-h-[60vh] flex flex-col items-center justify-center bg-white">
        <div className="w-20 h-20 rounded-3xl bg-[#FEF0EE] flex items-center justify-center mb-5">
          <WalletIcon className="w-9 h-9 text-[#E05D3A]" strokeWidth={2.2} />
        </div>
        <p className="text-[#6B6B6B] mb-4">{lang === "ar" ? "سجّل دخولك لعرض المحفظة" : "Sign in to view wallet"}</p>
        <button
          onClick={() => navigate("/login")}
          className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-3 font-bold"
          data-testid="wallet-login-cta"
        >
          {lang === "ar" ? "تسجيل الدخول" : "Sign in"}
        </button>
      </div>
    );
  }

  const visible = transactions.filter(t => filter === "all" ? true : t.type === filter);
  // Group by date
  const groups = visible.reduce((acc, tx) => {
    const dateKey = formatDate(tx.created_at, lang);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(tx);
    return acc;
  }, {});

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen pb-28">
      {/* Top bar */}
      <div className="px-5 pt-6 pb-2 flex items-center">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="wallet-back-btn" aria-label="back">
          <Chevron className="w-7 h-7 text-[#E05D3A]" strokeWidth={2.5} />
        </button>
      </div>

      <h1 className="font-display text-3xl font-black text-[#1A1A1A] text-center pt-2 pb-8" data-testid="wallet-title">
        {lang === "ar" ? "المحفظة" : "Wallet"}
      </h1>

      {/* Balance card */}
      <div className="mx-5 mb-6 rounded-3xl p-6 text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DARK} 100%)` }}>
        <p className="text-white/80 text-sm mb-1">{lang === "ar" ? "إجمالي الرصيد" : "Total balance"}</p>
        <div className="flex items-center gap-2 mb-1" dir="ltr">
          <span className="font-display text-5xl font-black tracking-tight" data-testid="wallet-balance">
            {balance.toFixed(2)}
          </span>
          <Riyal className="w-7 h-7" />
        </div>
        <p className="text-white/70 text-xs">
          {lang === "ar" ? "بطاقة جايك" : "Jayek wallet"}
        </p>
      </div>

      {/* Action buttons */}
      <div className="px-5 pb-6 grid grid-cols-2 gap-3">
        <button
          onClick={() => setTopupOpen(true)}
          className="flex items-center justify-center gap-2 bg-[#E05D3A] text-white rounded-2xl py-3.5 font-bold text-[15px] active:bg-[#C24B2C] shadow-sm shadow-orange-200"
          data-testid="wallet-add-btn"
        >
          <Plus className="w-5 h-5" strokeWidth={2.5} />
          {lang === "ar" ? "إضافة رصيد" : "Add balance"}
        </button>
        <button
          onClick={() => setSendOpen(true)}
          className="flex items-center justify-center gap-2 border-2 border-[#E05D3A] text-[#E05D3A] rounded-2xl py-3.5 font-bold text-[15px] bg-white active:bg-[#FEF0EE]"
          data-testid="wallet-send-btn"
        >
          <Send className="w-5 h-5" strokeWidth={2.5} />
          {lang === "ar" ? "إرسال رصيد" : "Send balance"}
        </button>
      </div>

      <div className="h-2 bg-[#F5F5F5]" />

      {/* Operations header */}
      <div className="px-5 pt-5 pb-3">
        <h2 className="font-display text-2xl font-black text-[#1A1A1A] mb-4" data-testid="wallet-operations-title">
          {lang === "ar" ? "العمليات" : "Operations"}
        </h2>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2" dir={isRTL ? "rtl" : "ltr"}>
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                filter === f.key
                  ? "bg-[#FEF0EE] text-[#E05D3A] border border-[#E05D3A]"
                  : "bg-[#F2F2F2] text-[#6B6B6B] border border-transparent"
              }`}
              data-testid={`wallet-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Transactions list */}
      <div className="px-5">
        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#E05D3A]" />
          </div>
        ) : Object.keys(groups).length === 0 ? (
          <div className="text-center py-12" data-testid="wallet-empty">
            <div className="inline-flex w-16 h-16 rounded-full bg-[#FEF0EE] items-center justify-center mb-3">
              <WalletIcon className="w-7 h-7 text-[#E05D3A]" strokeWidth={1.8} />
            </div>
            <p className="text-[#8B8B8B] text-sm">
              {lang === "ar" ? "لا توجد عمليات بعد" : "No transactions yet"}
            </p>
          </div>
        ) : (
          Object.entries(groups).map(([date, items]) => (
            <div key={date} className="pt-5">
              <p className="text-sm text-[#8B8B8B] mb-2 text-start">{date}</p>
              <div>
                {items.map(tx => <TxRow key={tx.tx_id} tx={tx} lang={lang} />)}
              </div>
            </div>
          ))
        )}
      </div>

      <TopupSheet open={topupOpen} onClose={() => setTopupOpen(false)} onSuccess={(b) => { setBalance(b); loadWallet(); }} />
      <SendSheet open={sendOpen} onClose={() => setSendOpen(false)} onSuccess={(b) => { setBalance(b); loadWallet(); }} balance={balance} />
    </div>
  );
}
